<script lang="ts" setup>
import { Classroom } from "~/classes/models/classrooms/classroom.model";

const props = defineProps({
  classroom: {
    type: Object as PropType<Classroom>,
    required: true,
  },
});

const uid = useCurrentUID();

if (!uid || !props.classroom.id) {
  throw new Error("UID is not defined");
}

const userClassroomDataStore = useClassroomDataStore(uid, props.classroom.id);
const { numUngradedDocuments, assignments } = storeToRefs(
  userClassroomDataStore
);

const appUserDataStore = useAppUserDataStore();
const { hasIEWLMS, isPublicDemo } = storeToRefs(appUserDataStore);

const hasAssignmentsWithMissingChecklists = computed(() => {
  return assignments.value.some((assignment) => {
    return assignment.type != AssignmentType.general && assignment.checklistId == undefined;
  });
});

const isSyncing = ref(false);
const syncClassroomSubmissions = async () => {
  if (isPublicDemo.value) {
    useBaseAlert("Oops!", "You can't sync in demo mode.");
    return;
  }

  if(hasAssignmentsWithMissingChecklists.value) {
    useBaseToast("Oops! Some assignments are missing a checklist.", "error");
    return;
  }

  isSyncing.value = true;

  for (const assignment of assignments.value) {
    await assignment.syncSubmissions();
  }

  isSyncing.value = false;
};
</script>

<template>
  <NuxtLink :to="`/classrooms/${classroom.id}`" class="block w-full min-w-0">
    <div
      class="p-2 flex flex-row w-full min-w-0 items-start justify-start cursor-pointer hover:bg-light-default overflow-clip relative rounded"
    >
      <BaseIconClassrooms size="25" class="mr-2 h-[24px] shrink-0" />
      <div class="flex min-w-0 flex-col items-start justify-start grow">
        <label
          class="block w-full truncate text-left text-h6 cursor-pointer"
        >
          {{ classroom.label }}
        </label>
        <div class="flex flex-row items-center mt-1 text-body-2">
          <span v-if="numUngradedDocuments && numUngradedDocuments > 0">
            Ungraded ({{ numUngradedDocuments }})
          </span>
          <BaseLinkButton
            v-if="hasIEWLMS(classroom.organizationId) != true"
            :show-spinner="isSyncing"
            class="!p-0"
            :class="{
              'ml-2': numUngradedDocuments && numUngradedDocuments > 0,
            }"
            color="primary"
            v-tooltip="'Sync submissions'"
            @click.stop.prevent="syncClassroomSubmissions"
          >
            <BaseIconSync class="mr-1" size="12" />
            <span> Sync </span>
          </BaseLinkButton>
        </div>
        <span
          v-if="
            numUngradedDocuments == undefined ||
            (numUngradedDocuments == 0 &&
              hasIEWLMS(classroom.organizationId) == true)
          "
        >
          <div
            class="flex flex-row items-center text-muted-foreground text-body-2"
          >
            <span> No Submissions </span>
          </div>
        </span>
      </div>
      <BaseIconInfo v-if="hasAssignmentsWithMissingChecklists"
        class="text-danger"
        v-tooltip="'Some assignments are missing a checklist.'"
      />
    </div>
  </NuxtLink>
</template>

<style scoped></style>
